// packages/web/src/components/account/subscription-panel.tsx
/**
 * SubscriptionPanel — student monthly card status + manage.
 *
 * 19BN Phase D D1: the subscription tab of AccountDrawer. Two states:
 *   - No active sub: show the plan card + "开通订阅" CTA (POST /subscription/create)
 *   - Active sub: show plan + period end + "取消订阅" (POST /subscription/cancel)
 *
 * The create endpoint grants 2000 monthly points immediately (billing.ts:204);
 * after success we refreshBalance so the hero updates without a manual refresh.
 * Real payment integration lands when merchant keys are provisioned (Phase B
 * decision — create currently grants directly for pilot).
 *
 * Spec: docs/architecture/account-system-design.md §2.4, §9.4
 */

import { useEffect, useState } from 'react';
import { useStore } from '../../lib/store/index.js';
import { subscriptionApi } from '../../api/subscription.js';
import { IngestApiError } from '../../lib/api-client.js';

const MONTHLY_CREDITS = 2000;
const PLAN_LABEL = '学生订阅 · 月度';

export function SubscriptionPanel() {
  const subscription = useStore((s) => s.subscription);
  const refreshSubscription = useStore((s) => s.refreshSubscription);
  const refreshBalance = useStore((s) => s.refreshBalance);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load on mount if not already loaded.
  useEffect(() => {
    if (subscription === null) void refreshSubscription();
  }, [subscription, refreshSubscription]);

  async function create() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await subscriptionApi.create();
      await Promise.all([refreshSubscription(), refreshBalance()]);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await subscriptionApi.cancel();
      await refreshSubscription();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // Active subscription view.
  if (subscription?.active) {
    const periodEnd = subscription.periodEnd ? new Date(subscription.periodEnd) : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
        <div
          style={{
            background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-hover))',
            color: 'var(--color-white)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-2xl)',
          }}
        >
          <div style={{ fontSize: 'var(--text-sm)', opacity: 0.8, marginBottom: 'var(--space-sm)' }}>
            {PLAN_LABEL}
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
            订阅中
          </div>
          {periodEnd && (
            <div style={{ fontSize: 'var(--text-sm)', opacity: 0.75, marginTop: 'var(--space-md)' }}>
              本期至 {periodEnd.toLocaleDateString()} · 每月初发放 {MONTHLY_CREDITS.toLocaleString()} 点
            </div>
          )}
        </div>
        {error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>{error}</div>}
        <button
          type="button"
          onClick={() => void cancel()}
          disabled={busy}
          style={{
            padding: 'var(--space-lg) var(--space-2xl)',
            background: 'var(--color-surface)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            cursor: busy ? 'not-allowed' : 'pointer',
            fontWeight: 500,
          }}
        >
          {busy ? '处理中...' : '取消订阅'}
        </button>
      </div>
    );
  }

  // No active subscription view.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-hover))',
          color: 'var(--color-white)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-2xl)',
        }}
      >
        <div style={{ fontSize: 'var(--text-sm)', opacity: 0.8, marginBottom: 'var(--space-sm)' }}>
          {PLAN_LABEL}
        </div>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-3xl)',
            fontWeight: 700,
            margin: 'var(--space-sm) 0',
          }}
        >
          ¥18<span style={{ fontSize: 'var(--text-md)', opacity: 0.7, fontWeight: 400 }}>/月</span>
        </div>
        <div style={{ fontSize: 'var(--text-sm)', opacity: 0.85 }}>
          每月初自动发放 {MONTHLY_CREDITS.toLocaleString()} 点 · 约覆盖 19 次完整生成
        </div>
      </div>
      <ul style={{ margin: 0, paddingLeft: 'var(--space-xl)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
        <li>每月初发放 {MONTHLY_CREDITS.toLocaleString()} 点，自动续费</li>
        <li>点数与充值点数合并使用，无过期</li>
        <li>可随时取消，取消后下月不再扣费</li>
      </ul>
      {error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>{error}</div>}
      <button
        type="button"
        onClick={() => void create()}
        disabled={busy}
        style={{
          width: '100%',
          padding: 'var(--space-lg)',
          background: 'var(--color-action)',
          color: 'var(--color-white)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-lg)',
          fontWeight: 600,
          cursor: busy ? 'not-allowed' : 'pointer',
        }}
      >
        {busy ? '开通中...' : '开通订阅'}
      </button>
    </div>
  );
}

function errMessage(err: unknown): string {
  if (err instanceof IngestApiError) return err.response.message ?? err.response.error;
  if (err instanceof Error) return err.message;
  return '操作失败';
}
